import { useParams } from 'react-router'

import { appMountPath } from '~/shell/app/appMountPath'
import { getFromAppNavCache } from '~/shell/app/appNav'
import { appById } from '~/shell/app/registry'
import { useActiveApp } from '~/shell/app/useActiveApp'

/**
 * The active app's primary nav.
 *
 * The shell resolves *which* app's nav and where it sits; everything inside it is the app's
 * (Atlas ADR-0021). The app is told its mount path rather than deriving one, and the nav is keyed on customer
 * as well as app, so switching either remounts it rather than reconciling one customer's items into
 * another's — which for Scout would leave the previous customer's category labels and patient counts on
 * screen with hrefs already pointing at the new one.
 *
 * No Suspense boundary and no lazy component. The app's own route load caches the nav, and the router
 * awaits that load before committing the navigation that renders this, so the cache is already filled
 * on the first render; a `lazy` component would suspend anyway and cost a flat 300ms of react-dom
 * reveal throttling for code that had already arrived. `appNav.ts` carries the mechanism.
 *
 * A copy of arc's `shell/app/header/ActiveAppNav.tsx`, deliberately the same module in the same place,
 * so Scout's move into the suite deletes this file rather than reconciling it.
 */
export const ActiveAppNav = () => {
  const { customer } = useParams<{ customer: string }>()
  const activeApp = useActiveApp()
  const Nav = getFromAppNavCache(appById(activeApp))

  if (Nav === undefined || customer === undefined || activeApp === undefined) {
    return null
  }

  return (
    <Nav
      key={`${customer}/${activeApp}`}
      basePath={appMountPath(customer, activeApp)}
    />
  )
}
